import { useState, useCallback, useMemo } from "react";
import type { Task, TaskId, SubtaskId, PlanEvent, EventId, Category } from "../../../shared/types";
import { createTask, createSubtask, createPlanEvent } from "../../../shared/types/factories";
import { track } from "../../../lib/analytics";

function dayOffset(offset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().split("T")[0];
}

const SEED_TASKS: Task[] = [
  { ...createTask("Livrer la feature paiement Stripe", "professional"), status: "doing", priority: "urgent", tags: ["client", "sprint-12"],
    subtasks: [
      { ...createSubtask("Intégrer le SDK Stripe"), done: true },
      { ...createSubtask("Gérer les webhooks"), done: true },
      createSubtask("Tester le parcours complet"),
      createSubtask("Déployer en staging"),
    ] },
  { ...createTask("Corriger le bug du panier vide en prod", "professional"), status: "todo", priority: "urgent", tags: ["bug"] },
  { ...createTask("Renouveler le passeport", "personal"), status: "todo", priority: "urgent", dueDate: dayOffset(3) },
  { ...createTask("Préparer les slides demo client", "professional"), status: "todo", priority: "normal", dueDate: dayOffset(1) },
  { ...createTask("Acheter le cadeau d'anniversaire de Lea", "personal"), status: "todo", priority: "normal", dueDate: dayOffset(4) },
  { ...createTask("Finir la lecture des Actes", "spiritual"), status: "doing", priority: "normal" },
  { ...createTask("Envoyer le compte-rendu du sprint", "professional"), status: "done", priority: "normal" },
  { ...createTask("Faire les courses de la semaine", "personal"), status: "done", priority: "normal" },
];

const SEED_EVENTS: PlanEvent[] = [
  createPlanEvent("Révision du cahier des charges", "professional", dayOffset(-2), "09:00", "10:30"),
  createPlanEvent("Appel avec le client — point projet", "professional", dayOffset(-2), "11:00", "11:45"),
  createPlanEvent("Pause déjeuner", "personal", dayOffset(-2), "12:00", "13:00"),
  createPlanEvent("Développement feature paiement", "professional", dayOffset(-2), "13:30", "16:00"),
  createPlanEvent("Course à pied", "personal", dayOffset(-2), "18:00", "19:00"),
  createPlanEvent("Stand-up équipe", "professional", dayOffset(-1), "09:00", "09:20"),
  createPlanEvent("Intégration API Stripe", "professional", dayOffset(-1), "09:30", "12:00"),
  createPlanEvent("Déjeuner avec Karim", "personal", dayOffset(-1), "12:30", "13:30"),
  createPlanEvent("Écriture tests unitaires", "professional", dayOffset(-1), "14:00", "16:00"),
  createPlanEvent("Lecture — Actes des apôtres", "spiritual", dayOffset(-1), "21:00", "21:40"),
  createPlanEvent("Stand-up équipe", "professional", dayOffset(0), "09:00", "09:20"),
  createPlanEvent("Correction bug checkout", "professional", dayOffset(0), "09:30", "11:00"),
  createPlanEvent("Revue de code avec Thomas", "professional", dayOffset(0), "11:15", "12:00"),
  createPlanEvent("Déjeuner", "personal", dayOffset(0), "12:15", "13:15"),
  createPlanEvent("Call design — maquettes mobile", "professional", dayOffset(0), "14:00", "15:00"),
  createPlanEvent("Deploy staging + QA", "professional", dayOffset(0), "15:30", "16:30"),
  createPlanEvent("Musculation", "personal", dayOffset(0), "18:30", "19:30"),
  createPlanEvent("Étude biblique — Romains 12", "spiritual", dayOffset(0), "21:00", "21:45"),
  createPlanEvent("Préparation demo client", "professional", dayOffset(1), "09:00", "10:00"),
  createPlanEvent("Demo produit — sprint review", "professional", dayOffset(1), "10:30", "11:30"),
  createPlanEvent("Rétro sprint avec l'équipe", "professional", dayOffset(1), "11:45", "12:30"),
  createPlanEvent("Déjeuner rapide", "personal", dayOffset(1), "12:30", "13:15"),
  createPlanEvent("Rédiger le compte-rendu sprint", "professional", dayOffset(1), "14:00", "15:00"),
  createPlanEvent("Natation", "personal", dayOffset(1), "17:30", "18:30"),
  createPlanEvent("Veille techno + lecture articles", "professional", dayOffset(2), "09:00", "10:00"),
  createPlanEvent("Refacto module auth", "professional", dayOffset(2), "10:15", "12:00"),
  createPlanEvent("Déjeuner avec l'équipe", "personal", dayOffset(2), "12:15", "13:15"),
  createPlanEvent("Planning semaine prochaine", "professional", dayOffset(2), "14:00", "14:45"),
  createPlanEvent("Rendez-vous dentiste", "personal", dayOffset(2), "16:00", "17:00"),
  createPlanEvent("Grasse matinée + petit-déj", "personal", dayOffset(3), "09:30", "10:30"),
  createPlanEvent("Courses marché", "personal", dayOffset(3), "11:00", "12:00"),
  createPlanEvent("Brunch avec Lea et Marc", "personal", dayOffset(3), "12:30", "14:00"),
  createPlanEvent("Lecture — suite Actes des apôtres", "spiritual", dayOffset(3), "16:00", "17:00"),
  createPlanEvent("Culte", "spiritual", dayOffset(4), "10:00", "12:00"),
  createPlanEvent("Repas en famille", "personal", dayOffset(4), "12:30", "14:00"),
  createPlanEvent("Temps calme — préparation semaine", "personal", dayOffset(4), "16:00", "17:00"),
];

export function useDemoData() {
  const [tasks, setTasks] = useState<Task[]>(SEED_TASKS);
  const [events, setEvents] = useState<PlanEvent[]>(SEED_EVENTS);

  const today = dayOffset(0);

  // Tasks
  const addTask = useCallback((title: string, category: Category, dueDate?: string | null) => {
    setTasks((prev) => [createTask(title, category, dueDate), ...prev]);
    track("demo_task_created", {});
  }, []);

  const cycleStatus = useCallback((id: TaskId) => {
    const cycle = { todo: "doing", doing: "done", done: "todo" } as const;
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status: cycle[t.status] } : t)));
    track("demo_task_status_changed", {});
  }, []);

  const updateTask = useCallback((id: TaskId, updates: Partial<Pick<Task, "title" | "priority" | "notes" | "tags" | "dueDate">>) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t)));
    track("demo_task_updated", {});
  }, []);

  const deleteTask = useCallback((id: TaskId) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    track("demo_task_deleted", {});
  }, []);

  const addSubtask = useCallback((taskId: TaskId, title: string) => {
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, subtasks: [...t.subtasks, createSubtask(title)] } : t));
    track("demo_subtask_added", {});
  }, []);

  const toggleSubtask = useCallback((taskId: TaskId, subtaskId: SubtaskId) => {
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, subtasks: t.subtasks.map((s) => s.id === subtaskId ? { ...s, done: !s.done } : s) } : t));
    track("demo_subtask_toggled", {});
  }, []);

  const deleteSubtask = useCallback((taskId: TaskId, subtaskId: SubtaskId) => {
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, subtasks: t.subtasks.filter((s) => s.id !== subtaskId) } : t));
  }, []);

  // Events
  const addEvent = useCallback((title: string, category: Category, date: string, startTime: string, endTime: string) => {
    setEvents((prev) => [...prev, createPlanEvent(title, category, date, startTime, endTime)]);
    track("demo_event_created", {});
  }, []);

  const deleteEvent = useCallback((id: EventId) => {
    setEvents((prev) => prev.filter((e) => e.id !== id));
    track("demo_event_deleted", {});
  }, []);

  const updateEvent = useCallback((id: EventId, updates: Partial<Pick<PlanEvent, "title" | "date" | "startTime" | "endTime" | "notes" | "description" | "tags" | "recurrence" | "reminderMinutes" | "category">>) => {
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...updates } : e)));
    track("demo_event_updated", {});
  }, []);

  // Derived for hero
  const todayEvents = useMemo(
    () => events.filter((e) => e.date === today).sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [events, today],
  );

  const doingTasks = useMemo(
    () => tasks.filter((t) => t.status === "doing" && !t.archived),
    [tasks],
  );

  return {
    tasks,
    events,
    todayEvents,
    doingTasks,
    addTask,
    cycleStatus,
    updateTask,
    deleteTask,
    addSubtask,
    toggleSubtask,
    deleteSubtask,
    addEvent,
    deleteEvent,
    updateEvent,
  };
}
